import { useState } from "react";
import { API, api } from "@/lib/api";
import { Bank, Buildings, MagnifyingGlass, ArrowSquareOut, FilePdf, CheckCircle, Warning, X as XIcon } from "@phosphor-icons/react";
import { getToken } from "@/lib/authToken";
/**
 * Caja de Catastro español.
 * - Si el expediente ya tiene RC → muestra los datos + link al PDF descriptivo.
 * - Si el lookup automático devolvió "manual_required" (foral o nacional inaccesible) →
 *   ofrece el botón para abrir el catastro foral/sede, y un input para pegar la RC.
 * - Si hay múltiples opciones → permite elegir una y se descarga el PDF.
 * - Botón "Buscar en Catastro" siempre disponible para re-lanzar.
 */
export default function CatastroCard({ exp, onReload }) {
  const token = getToken();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [manualRC, setManualRC] = useState("");
  const [showManual, setShowManual] = useState(false);

  const rc = exp.referencia_catastral;
  const status = exp.catastro_status;
  const datos = exp.catastro_datos || {};
  const source = exp.catastro_source;
  const options = exp.catastro_options || [];
  const foralUrl = exp.catastro_url_foral;
  const reason = exp.catastro_reason;
  const attId = exp.catastro_attachment_id;

  const sourceLabel = {
    nacional: "Catastro nacional",
    bizkaia: "Catastro foral de Bizkaia",
    gipuzkoa: "Catastro foral de Gipuzkoa",
    araba: "Catastro foral de Álava",
    navarra: "Catastro foral de Navarra",
    manual: "Introducido manualmente",
  }[source] || "Catastro";

  const lookup = async () => {
    setBusy(true); setError(null);
    try {
      await api.post(`/expedientes/${exp.id}/catastro/lookup`, {});
      onReload?.();
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Error en la consulta");
    } finally { setBusy(false); }
  };

  const selectRC = async (rcSel) => {
    setBusy(true); setError(null);
    try {
      await api.post(`/expedientes/${exp.id}/catastro/select-rc`, { rc: rcSel });
      onReload?.();
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Error seleccionando RC");
    } finally { setBusy(false); }
  };

  const submitManual = async () => {
    if (!manualRC.trim()) return;
    setBusy(true); setError(null);
    try {
      await api.post(`/expedientes/${exp.id}/catastro/manual`, {
        rc: manualRC.trim(),
        source: source && source !== "nacional" ? source : null,
      });
      setShowManual(false); setManualRC("");
      onReload?.();
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Error guardando RC");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="catastro-card" className="mt-3 pt-3 border-t border-amber-200/60">
      <div className="flex items-center justify-between gap-2 mb-2">
        <label className="!text-[10px] !uppercase !tracking-widest !text-muted !font-semibold flex items-center gap-1.5">
          <Bank className="w-3 h-3" weight="fill" />
          Ref. catastro
        </label>
        <button
          type="button"
          onClick={lookup}
          disabled={busy}
          data-testid="catastro-lookup-btn"
          className="text-[10px] uppercase tracking-widest font-bold text-brand hover:text-brand-strong inline-flex items-center gap-1 disabled:opacity-50"
        >
          <MagnifyingGlass className="w-3.5 h-3.5" weight="bold" />
          {rc ? "Re-buscar" : "Buscar"}
        </button>
      </div>

      {/* Caso: RC fijada */}
      {rc && (
        <div className="bg-white border border-amber-300 rounded-lg p-3 space-y-2" data-testid="catastro-found">
          <div className="flex items-start justify-between gap-2">
            <div className="font-mono text-[13px] font-bold text-navy break-all" data-testid="catastro-rc-value">
              {rc}
            </div>
            <span className="text-[9px] uppercase tracking-widest font-semibold text-emerald-700 inline-flex items-center gap-1 shrink-0">
              <CheckCircle className="w-3 h-3" weight="fill" />
              {sourceLabel}
            </span>
          </div>

          {(datos.direccion_normalizada || datos.uso || datos.superficie_m2 || datos.ano_construccion) && (
            <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[11px] text-navy-soft pt-1 border-t border-amber-100">
              {datos.direccion_normalizada && (
                <div className="col-span-2"><span className="text-muted">Dirección normalizada:</span> {datos.direccion_normalizada}</div>
              )}
              {datos.uso && <div><span className="text-muted">Uso:</span> {datos.uso}</div>}
              {datos.superficie_m2 && <div><span className="text-muted">Superficie:</span> {datos.superficie_m2} m²</div>}
              {datos.ano_construccion && <div><span className="text-muted">Año:</span> {datos.ano_construccion}</div>}
              {datos.coeficiente_part && <div><span className="text-muted">Coef. part.:</span> {datos.coeficiente_part}</div>}
            </div>
          )}

          <div className="flex items-center gap-2 pt-1">
            {attId && (
              <a
                href={`${API}/expedientes/${exp.id}/attachments/${attId}/file?token=${token}`}
                target="_blank"
                rel="noreferrer"
                data-testid="catastro-pdf-link"
                className="inline-flex items-center gap-1 text-[11px] font-semibold text-brand hover:text-brand-strong"
              >
                <FilePdf className="w-3.5 h-3.5" weight="bold" />
                Ver descripción catastral
              </a>
            )}
            <button
              type="button"
              onClick={() => { setShowManual(true); setManualRC(rc); }}
              data-testid="catastro-edit-btn"
              className="ml-auto text-[10px] uppercase tracking-widest font-semibold text-muted hover:text-navy"
            >
              Editar RC
            </button>
          </div>

          {exp.catastro_map_attachment_id && (
            <div className="pt-2 border-t border-amber-100">
              <img
                src={`${API}/expedientes/${exp.id}/attachments/${exp.catastro_map_attachment_id}/file?token=${token}`}
                alt={`Mapa de ${rc}`}
                data-testid="catastro-map-img"
                className="w-full h-32 object-cover rounded border border-slate-200"
              />
              <div className="text-[9px] text-muted text-center mt-1">© OpenStreetMap contributors</div>
            </div>
          )}
        </div>
      )}

      {/* Caso: múltiples bienes */}
      {!rc && status === "multiple" && options.length > 0 && (
        <div className="bg-amber-50 border border-amber-300 rounded-lg p-3 space-y-2" data-testid="catastro-multiple">
          <div className="text-[11px] text-navy-soft">
            <Buildings className="w-3.5 h-3.5 inline mr-1" weight="fill" />
            Hay <b>{options.length}</b> bienes en este número. Selecciona uno:
          </div>
          <div className="space-y-1 max-h-48 overflow-auto">
            {options.map((opt) => (
              <button
                key={opt.rc}
                type="button"
                onClick={() => selectRC(opt.rc)}
                disabled={busy}
                data-testid={`catastro-option-${opt.rc}`}
                className="w-full text-left px-2 py-1.5 rounded border border-amber-200 hover:border-brand hover:bg-amber-100 text-[11px] disabled:opacity-50"
              >
                <div className="font-mono font-bold text-navy">{opt.rc}</div>
                {opt.label && opt.label !== opt.rc && (
                  <div className="text-muted text-[10px] mt-0.5">{opt.label}</div>
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Caso: requiere entrada manual (foral o nacional inaccesible) */}
      {!rc && status === "manual_required" && (
        <div className="bg-amber-50 border border-amber-300 rounded-lg p-3 space-y-2" data-testid="catastro-manual-required">
          <div className="text-[11px] text-navy-soft">
            <Warning className="w-3.5 h-3.5 inline mr-1 text-amber-600" weight="fill" />
            {reason || "Se requiere entrada manual"}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {foralUrl && (
              <a
                href={foralUrl}
                target="_blank"
                rel="noreferrer"
                data-testid="catastro-foral-link"
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded bg-brand text-white text-[11px] font-semibold hover:bg-brand-strong"
              >
                <ArrowSquareOut className="w-3.5 h-3.5" weight="bold" />
                Abrir {sourceLabel}
              </a>
            )}
            <button
              type="button"
              onClick={() => setShowManual((s) => !s)}
              data-testid="catastro-toggle-manual-btn"
              className="text-[11px] font-semibold text-brand hover:text-brand-strong underline underline-offset-2"
            >
              {showManual ? "Cancelar" : "Pegar RC manualmente"}
            </button>
          </div>
        </div>
      )}

      {/* Caso: no encontrado / pendiente */}
      {!rc && status === "not_found" && (
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-[11px] text-muted" data-testid="catastro-not-found">
          {reason || "No se encontró el inmueble en el catastro."}
          <button
            type="button"
            onClick={() => setShowManual(true)}
            data-testid="catastro-not-found-manual-btn"
            className="ml-2 text-brand hover:text-brand-strong underline underline-offset-2 font-semibold"
          >
            Introducir RC manualmente
          </button>
        </div>
      )}

      {!rc && !status && (
        <div className="bg-slate-50 border border-slate-200 border-dashed rounded-lg p-3 text-[11px] text-muted" data-testid="catastro-empty">
          Sin referencia catastral. Pulsa <b>Buscar</b> para localizar el inmueble en el Catastro español.
        </div>
      )}

      {/* Form manual (compartido) */}
      {showManual && (
        <div className="mt-2 bg-white border border-brand/40 rounded-lg p-3 space-y-2" data-testid="catastro-manual-form">
          <label className="text-[10px] uppercase tracking-widest font-semibold text-muted">
            Referencia catastral (20 caracteres)
          </label>
          <input
            type="text"
            value={manualRC}
            onChange={(e) => setManualRC(e.target.value.toUpperCase())}
            placeholder="0000000XX0000X0000XX"
            maxLength={20}
            data-testid="catastro-manual-input"
            className="w-full font-mono text-[12px] !py-1.5"
          />
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={submitManual}
              disabled={busy || manualRC.trim().length < 14}
              data-testid="catastro-manual-save-btn"
              className="px-3 py-1.5 rounded bg-brand text-white text-[11px] font-semibold hover:bg-brand-strong disabled:opacity-50"
            >
              Guardar RC
            </button>
            <button
              type="button"
              onClick={() => { setShowManual(false); setManualRC(""); }}
              data-testid="catastro-manual-cancel-btn"
              className="px-2 py-1.5 text-[11px] text-muted hover:text-navy inline-flex items-center gap-1"
            >
              <XIcon className="w-3 h-3" /> Cancelar
            </button>
          </div>
        </div>
      )}

      {error && (
        <div className="mt-2 text-[11px] text-red-600" data-testid="catastro-error">
          {error}
        </div>
      )}
    </div>
  );
}
